import { Text } from '@foss/legion-ui'
import { CodeBlock } from '../content/CodeBlock'
import { Workspace } from '../workspace/Workspace'
import prose from '../content/Prose.module.css'

const KNOBS: [string, string, string][] = [
  ['accent', '--legion-accent', 'The one hue the whole palette keys off.'],
  ['blur', '--legion-blur', 'Backdrop blur radius on glass surfaces.'],
  [
    'radiusScale',
    '--legion-radius-scale',
    'Multiplier on every corner radius.',
  ],
  ['scheme', 'data-theme', "'dark' | 'light' | 'system'."],
  ['tokens', 'any --legion-*', 'Escape hatch for individual tokens.'],
]

export function Theming() {
  return (
    <div className={prose.prose} data-testid="theming-page">
      <section className={prose.section}>
        <Text variant="heading" as="h2">
          The model
        </Text>
        <Text>
          There is no theme object and no style runtime. A theme is a set of{' '}
          <code>--legion-*</code> custom properties, and{' '}
          <code>LegionProvider</code> writes the ones you set onto an element.
          Everything else inherits through ordinary CSS.
        </Text>
        <Text>
          That is why providers nest: an inner one overrides its own keys and
          inherits the rest, so a light island inside a dark app is just a
          nested provider rather than a second theme object.
        </Text>
      </section>

      <section className={prose.section}>
        <Text variant="heading" as="h2">
          What you can set
        </Text>
        <div className={prose.tableWrap}>
          <table className={prose.table}>
            <thead>
              <tr>
                <th>Key</th>
                <th>Writes</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {KNOBS.map(([key, writes, note]) => (
                <tr key={key}>
                  <td>
                    <code>{key}</code>
                  </td>
                  <td>
                    <code>{writes}</code>
                  </td>
                  <td>{note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className={prose.section}>
        <Text variant="heading" as="h2">
          Scope
        </Text>
        <Text>
          <code>scope=&quot;subtree&quot;</code> (the default) writes to the
          provider&apos;s own element, which is what makes providers nestable.{' '}
          <code>scope=&quot;root&quot;</code> writes to{' '}
          <code>documentElement</code> instead, so portalled content is themed
          too. An app usually wants one root provider and, if anything, subtree
          providers inside it.
        </Text>
        <CodeBlock label="Nesting">{`<LegionProvider scope="root" theme={{ scheme: 'dark' }}>
  <App />
  <LegionProvider theme={{ scheme: 'light' }}>
    <Login />
  </LegionProvider>
</LegionProvider>`}</CodeBlock>
      </section>

      <section className={prose.section}>
        <Text variant="heading" as="h2">
          Reading the theme
        </Text>
        <Text>
          <code>useTheme()</code> returns CSS value strings, never resolved
          colours. A knob you set resolves to that literal; one you did not
          resolves to <code>var(--legion-…)</code>, which is still valid in any{' '}
          <code>style</code> prop and keeps the stylesheet the single source of
          truth.
        </Text>
        <Workspace
          name="Theming"
          source={`<Stack gap={4}>
  <LegionProvider theme={{ accent: '#aa4f29' }}>
    <Stack direction="row" gap={2} align="center">
      <Button>Custom accent</Button>
      <Chip variant="accent">Chip</Chip>
    </Stack>
  </LegionProvider>
  <LegionProvider theme={{ scheme: 'light' }}>
    <Stack direction="row" gap={2} align="center">
      <Button>Light island</Button>
      <Text tone="muted">nested provider</Text>
    </Stack>
  </LegionProvider>
</Stack>`}
        />
      </section>
    </div>
  )
}
